// A '.tsx' file enables JSX support in the TypeScript compiler, 
// for more information see the following page on the TypeScript wiki:
// https://github.com/Microsoft/TypeScript/wiki/JSX

import React, { createRef } from 'react';
interface NavigationBreadcrumbPanelProps extends React.PropsWithChildren<{}> {
    breadcrumbList: any[];
    parameterMap: any;
    displayProp: string;
    keyProps: string[];
    rootCrumbTitle?: string;
    showLastItem?: boolean;
    showLastItemChildren?: boolean;
    lastItemIsStandardCrumb?: boolean;
    highlightActiveChildInList?: boolean;
    onBreadcrumbClick: (e: any, crumb: INamedItem) => void;
    onBreadcrumbChildClick: (e: any, parentCrumb: INamedItem, childItem: INamedItem) => void;
    onGetSeparatorListData: (requestData: any, callback: (data: INamedItem[]) => void) => void;
    onRootClick?: (e: any) => void;
    onGetHRef?: (crumb: INamedItem) => string;
    onGetRootHRef?: () => string;
}

function createNavigationCrumbs(props: NavigationBreadcrumbPanelProps) {
    var crumbs: any[] = [];
    if (props.rootCrumbTitle) {
        let rootCrumbHRef = props.onGetRootHRef ? props.onGetRootHRef() : null;
        crumbs.push(<a
            className="crumb"
            onClick={(e) => props.onRootClick(e)}
            href={rootCrumbHRef}
            key={props.rootCrumbTitle}>
            {props.rootCrumbTitle}
        </a>);
        crumbs.push(<Separator
            selected={(props.highlightActiveChildInList) ? props.breadcrumbList[0] : null}
            keyProps={props.keyProps}
            onClick={props.onBreadcrumbChildClick}
            onGetHRef={props.onGetHRef}
            onGetSeparatorListData={props.onGetSeparatorListData}
            displayProp="Name"
            key={`${rootCrumbHRef}-SEPARATOR`}
        />);
    }

    if (props.breadcrumbList.length > 0) {
        var numBreadCrumbs = props.breadcrumbList.length - 1;
        if (props.lastItemIsStandardCrumb)
            numBreadCrumbs++;

        for (let i = 0; i < numBreadCrumbs; i++) {
            let crumb = props.breadcrumbList[i];
            let params: any = {};
            for (let prop in props.parameterMap) {
                params[prop] = crumb[props.parameterMap[prop]];
            }

            crumbs.push(<a
                className='crumb'
                onClick={(e) => props.onBreadcrumbClick(e, crumb)}
                href={props.onGetHRef ? props.onGetHRef(crumb) : null}
                key={crumb.ID}>{crumb[props.displayProp]}
            </a>);
            crumbs.push(<Separator
                parameters={params}
                selected={(props.highlightActiveChildInList) ? props.breadcrumbList[i + 1] : null}
                keyProps={props.keyProps}
                onClick={props.onBreadcrumbChildClick}
                onGetHRef={props.onGetHRef}
                onGetSeparatorListData={props.onGetSeparatorListData}
                displayProp="Name"
                key={`${crumb.ID}-SEPARATOR`}
                parentItem={crumb}
            />);
        }

        if (props.showLastItem && !props.lastItemIsStandardCrumb) {
            let lastCrumbParams: any = {};
            let lastCrumb = props.breadcrumbList[props.breadcrumbList.length - 1];
            for (let prop in props.parameterMap) {
                lastCrumbParams[prop] = lastCrumb[props.parameterMap[prop]];
            }

            crumbs.push(<span
                className='crumb'
                key={`${lastCrumb.ID}-LAST`}>{props.breadcrumbList[props.breadcrumbList.length - 1][props.displayProp]}
            </span>);

            if (props.showLastItemChildren) {
                crumbs.push(<Separator
                    parameters={lastCrumbParams}
                    selected={(props.highlightActiveChildInList) ? props.breadcrumbList[props.breadcrumbList.length - 1] : null}
                    keyProps={props.keyProps}
                    onClick={props.onBreadcrumbChildClick}
                    onGetHRef={props.onGetHRef}
                    onGetSeparatorListData={props.onGetSeparatorListData}
                    displayProp="Name"
                    key={`${lastCrumb.ID}-SEPARATOR-LAST`}
                    parentItem={lastCrumb}
                />);
            }
        }
    }
    return crumbs;
}

const NavigationBreadcrumbPanel = (props: NavigationBreadcrumbPanelProps) => (
    <span className='navigation-breadcrumb-panel'>
        { createNavigationCrumbs(props) }
    </span>
);

interface SeparatorProps extends React.PropsWithChildren<{}> {
    parameters?: any;
    displayProp: string;
    keyProps: string[];
    selected: any;
    parentItem?: INamedItem;
    onClick: (e: any, parentCrumb: INamedItem, childItem: INamedItem) => void;
    onGetSeparatorListData: (requestData: any, callback: (data: INamedItem[]) => void) => void;
    onGetHRef?: (crumb: INamedItem) => string;
}

interface SeparatorState {
    separatorList?: any[];
    collapsed: boolean;
    randId?: string;
}

class Separator extends React.Component<SeparatorProps, SeparatorState> {
    _sepRef = createRef<HTMLUListElement>();
    constructor(props: SeparatorProps, context: any) {
        super(props, context);

        this.state = {
            collapsed: true,
            separatorList: [],
            randId: (Math.random() + (new Date()).getTime()).toString().replace('.', '-')
        };
    }

    private _toggleMenu = () => {
        if (this.state.separatorList.length === 0) {
            this.props.onGetSeparatorListData(this.props.parameters, (data) => {
                this.setState({
                    collapsed: !this.state.collapsed,
                    separatorList: data
                })
            });
        }
        else {
            this.setState({
                collapsed: !this.state.collapsed
            });
        }
    }

    private _handleClick = (event: Event) => {
        if (!this.state.collapsed && !$(event.target).is(`#${this.state.randId}-CONT`) && !$(event.target).is(`#${this.state.randId}-SEP`)) {
            this.setState({
                collapsed: !this.state.collapsed
            });
        }
    }

    componentDidUpdate() {
        let contentElement = this._sepRef.current as HTMLElement;

        // contentElement.offsetParent will be null if the element is
        // currently not visible
        if (contentElement && contentElement.offsetParent) {

            // Reset any previous element re-positioning
            if (contentElement.style.left) {
                contentElement.style.left = "";
            }

            let parent = contentElement.parentElement;

            // Find the first DIV in the parent hierarchy chain. We'll use it as
            // a bounding area for the separator popup.
            while (parent && parent.tagName != "DIV") {
                parent = parent.parentElement;
            }

            if (parent) {
                let parentRect = parent.getBoundingClientRect();
                let contentRect = contentElement.getBoundingClientRect();

                if (contentRect.left < parentRect.left) {
                    let offParent = Math.round(parentRect.left - contentRect.left);
                    let newLeft = contentElement.offsetLeft + offParent;
                    contentElement.style.left = newLeft.toString() + "px";
                }
                else if (contentRect.right > parentRect.right) {
                    let offParent = Math.round(parentRect.right - contentRect.right);
                    let newLeft = contentElement.offsetLeft + offParent;
                    contentElement.style.left = newLeft.toString() + "px";
                }
            }
        }
    }

    UNSAFE_componentWillMount() {
        window.addEventListener("click", this._handleClick);
    }

    componentWillUnmount() {
        window.removeEventListener("click", this._handleClick);
    }

    render() {
        let selected = this.props.selected;
        let separatorList = this.state.separatorList;

        if (!this.state.collapsed && separatorList.length == 0) {
            separatorList = [{ Name: `${InovxLoader.t("common:message.emptyList")}`, ID: null }];
        }

        let folderList = separatorList.map((item) => {
            let matching = true;
            for (let i = 0; i < this.props.keyProps.length; i++) {
                let prop = this.props.keyProps[i];
                if (!selected || selected[prop] !== item[prop]) {
                    matching = false;
                    break;
                }
            };
            let itemKey = (item.ID) ? item.ID : (this.props.parentItem) ? this.props.parentItem.ID : "ROOT";
            if (matching) {
                return <ListItem
                    initialSelected={true}
                    display={item[this.props.displayProp]}
                    onClick={this.props.onClick}
                    onGetHRef={this.props.onGetHRef}
                    item={item}
                    parentItem={this.props.parentItem}
                    key={`${itemKey}-CHILD`}
                />
            } else {
                return <ListItem
                    initialSelected={false}
                    display={item[this.props.displayProp]}
                    onClick={this.props.onClick}
                    onGetHRef={this.props.onGetHRef}
                    item={item}
                    parentItem={this.props.parentItem}
                    key={`${itemKey}-CHILD`}
                />
            };
        });

        return (
            <span className="separator-wrapper">
                <span className={`separator-container clickable`} onClick={this._toggleMenu} id={`${this.state.randId}-CONT`}>
                    <span className={`separator ${this.state.collapsed ? "" : "expanded"}`} id={`${this.state.randId}-SEP`}>&rsaquo;</span>
                </span>
                <ul className={`dropdown-content ${this.state.collapsed ? "" : "show"}`} ref={this._sepRef}>
                    {folderList}
                </ul>
            </span>
        )
    }
}


interface ListItemProps extends React.PropsWithChildren<{}> {
    display: any;
    initialSelected: boolean;
    item: INamedItem;
    parentItem: INamedItem;
    onClick: (e: any, parentCrumb: INamedItem, childItem: INamedItem) => void;
    onGetHRef?: (crumb: INamedItem) => string;
}

interface ListItemState {
    selected?: boolean;
}

class ListItem extends React.Component<ListItemProps, ListItemState> {
    constructor(props: ListItemProps) {
        super(props);

        this.state = {
            selected: this.props.initialSelected ? this.props.initialSelected : false
        };
    }
    private _onClick = (e: any) => {
        if (e && !e.ctrlKey) {
            e.preventDefault();

            this.props.onClick(e, this.props.parentItem, this.props.item);
            this.setState({
                selected: !this.state.selected
            });
        }
    }

    render() {
        var optionalListItemProps: any = {};
        var isFakeItem = (this.props.item.ID) ? false : true;

        if (!this.state.selected && !isFakeItem) {
            optionalListItemProps.onClick = (e: React.MouseEvent) => this.props.onClick(e, this.props.parentItem, this.props.item);
            optionalListItemProps.href = this.props.onGetHRef ? this.props.onGetHRef(this.props.item) : null;
        }
        
        return (
            <li>
                <a className={`breadcrumb-list-item ${this.state.selected ? "breadcrumb-selected" : ""}`}
                    title={this.props.display}
                    {...optionalListItemProps}>{this.props.display}
                </a>
            </li>);
    }
}

export default NavigationBreadcrumbPanel;